import React, { useState, useEffect, useMemo } from 'react'
import {
  Table,
  Input,
  Button,
  Tag,
  Tooltip,
  Modal,
  Skeleton,
  message,
  Select,
} from 'antd'
import { SearchOutlined, EyeOutlined, MailOutlined } from '@ant-design/icons'
import styled from 'styled-components/macro'
import { supplierPortalTheme } from 'src/branding/supplierPortalTheme'
import moment from 'moment'
import { API } from 'src/utils/interceptor'
import { useSelector } from 'react-redux'

const { Option } = Select

const SentEmailsTab = () => {
  const account = useSelector(state => state.auth.account)
  const [emails, setEmails] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchText, setSearchText] = useState('')
  const [dateFilter, setDateFilter] = useState('all') // 'all', 'last30'
  const [selectedEmail, setSelectedEmail] = useState(null)
  const [detailsModalVisible, setDetailsModalVisible] = useState(false)

  // Supplier IDs available to the user
  const supplierIds = useMemo(() => {
    return account?.supplier?.map(s => s._id).filter(Boolean) || []
  }, [account])

  // Use first supplier ID
  const supplierId = supplierIds.length > 0 ? supplierIds[0] : null

  // Fetch emails on mount
  useEffect(() => {
    if (supplierId) {
      fetchEmails()
    }
  }, [supplierId])

  const fetchEmails = async () => {
    setLoading(true)
    try {
      const response = await API.get('api2', 'supplier/marketing/emails')
      if (response.success) {
        setEmails(response.emails || [])
      } else {
        message.error('Failed to load emails')
      }
    } catch (error) {
      console.error('Error fetching emails:', error)
      message.error('Failed to load emails. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Filter emails based on search and date filter
  const filteredEmails = useMemo(() => {
    let filtered = emails

    // Apply date filter
    if (dateFilter === 'last30') {
      const thirtyDaysAgo = moment().subtract(30, 'days')
      filtered = filtered.filter(email =>
        moment(email.sentAt).isAfter(thirtyDaysAgo),
      )
    }

    // Apply search filter
    if (searchText.trim()) {
      const searchTerm = searchText.toLowerCase().trim()
      filtered = filtered.filter(email => {
        const subjectMatch = email.subject?.toLowerCase().includes(searchTerm)
        const emailBodyMatch = email.emailBody
          ?.toLowerCase()
          .includes(searchTerm)
        const recipientMatch = email.recipients?.some(r =>
          r.emailAddress?.toLowerCase().includes(searchTerm),
        )
        return subjectMatch || emailBodyMatch || recipientMatch
      })
    }

    return filtered
  }, [emails, searchText, dateFilter])

  // Get unique recipients (deduplicate by email address)
  const getUniqueRecipients = recipients => {
    if (!recipients || recipients.length === 0) return []

    const seen = new Map()
    recipients.forEach(recipient => {
      const email = recipient.emailAddress?.toLowerCase()
      if (email) {
        if (!seen.has(email)) {
          seen.set(email, recipient)
        } else {
          // If we've seen this email, keep the one with 'sent' status if current is 'failed'
          const existing = seen.get(email)
          if (existing.status === 'failed' && recipient.status === 'sent') {
            seen.set(email, recipient)
          }
        }
      }
    })

    return Array.from(seen.values())
  }

  // Calculate status counts for each email (using unique recipients)
  const getStatusCounts = email => {
    const uniqueRecipients = getUniqueRecipients(email.recipients)
    const sentCount =
      uniqueRecipients.filter(r => r.status === 'sent').length || 0
    const failedCount =
      uniqueRecipients.filter(r => r.status === 'failed').length || 0
    return { sentCount, failedCount, uniqueCount: uniqueRecipients.length }
  }

  // Get send type badge color
  const getSendTypeColor = sendType => {
    switch (sendType) {
      case 'one-to-one':
        return 'blue'
      case 'bulk':
        return 'green'
      case 'broadcast':
        return 'orange'
      default:
        return 'default'
    }
  }

  // Format send type for display
  const formatSendType = sendType => {
    switch (sendType) {
      case 'one-to-one':
        return 'One-to-One'
      case 'bulk':
        return 'Bulk'
      case 'broadcast':
        return 'Broadcast'
      default:
        return sendType
    }
  }

  const handleViewDetails = email => {
    setSelectedEmail(email)
    setDetailsModalVisible(true)
  }

  const columns = [
    {
      title: 'Sent At',
      dataIndex: 'sentAt',
      key: 'sentAt',
      width: 180,
      render: date => (date ? moment(date).format('DD MMM YYYY, HH:mm') : '—'),
      sorter: (a, b) => moment(a.sentAt).valueOf() - moment(b.sentAt).valueOf(),
    },
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
      ellipsis: {
        showTitle: false,
      },
      render: text => (
        <Tooltip placement='topLeft' title={text}>
          <SubjectText>{text || '—'}</SubjectText>
        </Tooltip>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'sendType',
      key: 'sendType',
      width: 120,
      render: sendType => (
        <Tag color={getSendTypeColor(sendType)}>{formatSendType(sendType)}</Tag>
      ),
    },
    {
      title: 'Recipients',
      key: 'recipients',
      width: 150,
      render: (_, record) => {
        const { sentCount, failedCount } = getStatusCounts(record)
        if (failedCount > 0) {
          return (
            <RecipientsText>
              <span style={{ color: '#52c41a' }}>{sentCount} sent</span>,{' '}
              <span style={{ color: '#ff4d4f' }}>{failedCount} failed</span>
            </RecipientsText>
          )
        }
        return (
          <RecipientsText style={{ color: '#52c41a' }}>
            {sentCount} sent
          </RecipientsText>
        )
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Button
          type='link'
          icon={<EyeOutlined />}
          onClick={() => handleViewDetails(record)}
          aria-label={`View details for email "${
            record.subject || 'untitled'
          }" sent ${moment(record.sentAt).format('DD MMM YYYY')}`}
        >
          View Details
        </Button>
      ),
    },
  ]

  if (loading) {
    return (
      <TabWrapper>
        <Card>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      </TabWrapper>
    )
  }

  if (emails.length === 0) {
    return (
      <TabWrapper>
        <EmptyStateCard>
          <EmptyStateIcon>
            <MailOutlined />
          </EmptyStateIcon>
          <EmptyStateTitle>No emails have been sent yet</EmptyStateTitle>
          <EmptyStateText>
            Once you send emails to your customers, they&apos;ll appear here.
          </EmptyStateText>
        </EmptyStateCard>
      </TabWrapper>
    )
  }

  return (
    <TabWrapper>
      <FiltersContainer>
        <FilterGroup>
          <Select
            value={dateFilter}
            onChange={setDateFilter}
            style={{ width: 200 }}
          >
            <Option value='all'>All Time</Option>
            <Option value='last30'>Last 30 Days</Option>
          </Select>
        </FilterGroup>
        <FilterGroup>
          <Input
            placeholder='Search by subject or email address...'
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
        </FilterGroup>
      </FiltersContainer>

      <Card>
        <Table
          columns={columns}
          dataSource={filteredEmails}
          rowKey='_id'
          pagination={
            filteredEmails.length > 50
              ? {
                  pageSize: 20,
                  showSizeChanger: true,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} emails`,
                }
              : false
          }
          scroll={{ x: 'max-content' }}
        />
      </Card>

      <Modal
        title='Email Details'
        visible={detailsModalVisible}
        onCancel={() => {
          setDetailsModalVisible(false)
          setSelectedEmail(null)
        }}
        footer={[
          <Button key='close' onClick={() => setDetailsModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={800}
        keyboard={true}
        maskClosable={true}
      >
        {selectedEmail && (
          <DetailsContent>
            <DetailsSection>
              <DetailsLabel>Sent At:</DetailsLabel>
              <DetailsValue>
                {moment(selectedEmail.sentAt).format('DD MMM YYYY, HH:mm')}
              </DetailsValue>
            </DetailsSection>

            <DetailsSection>
              <DetailsLabel>Subject:</DetailsLabel>
              <DetailsValue>{selectedEmail.subject}</DetailsValue>
            </DetailsSection>

            <DetailsSection>
              <DetailsLabel>Type:</DetailsLabel>
              <DetailsValue>
                <Tag color={getSendTypeColor(selectedEmail.sendType)}>
                  {formatSendType(selectedEmail.sendType)}
                </Tag>
              </DetailsValue>
            </DetailsSection>

            <DetailsSection>
              <DetailsLabel>
                Recipients (
                {getUniqueRecipients(selectedEmail.recipients).length}):
              </DetailsLabel>
              <RecipientsList>
                {getUniqueRecipients(selectedEmail.recipients).map(
                  (recipient, index) => (
                    <RecipientItem key={index}>
                      <RecipientEmail>{recipient.emailAddress}</RecipientEmail>
                      <Tag
                        color={recipient.status === 'sent' ? 'green' : 'red'}
                        style={{ marginLeft: 8 }}
                      >
                        {recipient.status}
                      </Tag>
                    </RecipientItem>
                  ),
                )}
              </RecipientsList>
            </DetailsSection>

            <DetailsSection>
              <DetailsLabel>Email Body:</DetailsLabel>
              <EmailBodyPreview
                dangerouslySetInnerHTML={{ __html: selectedEmail.emailBody }}
              />
            </DetailsSection>
          </DetailsContent>
        )}
      </Modal>
    </TabWrapper>
  )
}

const TabWrapper = styled.div`
  --sp-color-surface: ${supplierPortalTheme.colors.surface};
  --sp-color-text: ${supplierPortalTheme.colors.text};
  --sp-color-muted: ${supplierPortalTheme.colors.muted};
  --sp-color-border: ${supplierPortalTheme.colors.border};
  --sp-radius-card: ${supplierPortalTheme.radii.card};
  --sp-shadow-card: ${supplierPortalTheme.shadows.card};
  --sp-space-xs: ${supplierPortalTheme.spacing.xs};
  --sp-space-sm: ${supplierPortalTheme.spacing.sm};
  --sp-space-md: ${supplierPortalTheme.spacing.md};
  --sp-space-lg: ${supplierPortalTheme.spacing.lg};
  --sp-space-xl: ${supplierPortalTheme.spacing.xl};
  --sp-space-xxl: ${supplierPortalTheme.spacing.xxl};
  width: 100%;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  gap: var(--sp-space-lg);
`

const Card = styled.div`
  background: var(--sp-color-surface);
  border-radius: var(--sp-radius-card);
  padding: var(--sp-space-lg);
  box-shadow: var(--sp-shadow-card);
  border: 1px solid var(--sp-color-border);

  .ant-table-thead > tr > th {
    background: #f7fafc;
    border-bottom: 1px solid var(--sp-color-border);
    font-weight: 600;
    color: var(--sp-color-text);
    padding: var(--sp-space-md);
  }

  .ant-table-tbody > tr {
    height: 48px;

    &:hover {
      background: #f7fafc;

      > td {
        background: #f7fafc !important;
      }
    }
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid var(--sp-color-border);
    padding: var(--sp-space-md);
  }
`

const FiltersContainer = styled.div`
  display: flex;
  gap: var(--sp-space-md);
  flex-wrap: wrap;
`

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
`

const SubjectText = styled.span`
  max-width: 300px;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const RecipientsText = styled.span`
  font-size: 14px;
`

const EmptyStateCard = styled.div`
  background: var(--sp-color-surface);
  border-radius: var(--sp-radius-card);
  padding: var(--sp-space-xxl);
  box-shadow: var(--sp-shadow-card);
  border: 1px solid var(--sp-color-border);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-space-sm);
  min-height: 320px;
  justify-content: center;
`

const EmptyStateIcon = styled.div`
  font-size: 48px;
  color: var(--sp-color-muted);
`

const EmptyStateTitle = styled.h3`
  margin: 0;
  color: var(--sp-color-text);
  font-size: 18px;
  font-weight: 600;
`

const EmptyStateText = styled.p`
  margin: 0;
  color: var(--sp-color-muted);
  font-size: 15px;
  line-height: 1.5;
`

const DetailsContent = styled.div`
  max-height: 70vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: var(--sp-space-md);
  padding: var(--sp-space-xs) 0;
`

const DetailsSection = styled.div`
  margin-bottom: var(--sp-space-md);

  &:last-child {
    margin-bottom: 0;
  }
`

const DetailsLabel = styled.div`
  font-weight: 600;
  color: var(--sp-color-text);
  margin-bottom: var(--sp-space-sm);
  font-size: 14px;
`

const DetailsValue = styled.div`
  color: var(--sp-color-text);
  font-size: 14px;
`

const RecipientsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-space-sm);
  max-height: 200px;
  overflow-y: auto;
  padding: var(--sp-space-sm);
  background: #f8fafc;
  border-radius: 4px;
`

const RecipientItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const RecipientEmail = styled.span`
  color: var(--sp-color-text);
  font-size: 14px;
`

const EmailBodyPreview = styled.div`
  border: 1px solid var(--sp-color-border);
  border-radius: 4px;
  padding: var(--sp-space-md);
  background: #ffffff;
  max-height: 400px;
  overflow-y: auto;

  /* Ensure email HTML renders properly */
  img {
    max-width: 100%;
    height: auto;
  }
`

export default SentEmailsTab
